import { Header, Icon, type IconName, MobileShell } from "@caint/design-system";
import { Fragment, useCallback, useState } from "react";
import { MatrizUpload } from "../components/MatrizUpload.tsx";
import { ResultadosPublico } from "../components/ResultadosPublico.tsx";
import { BarraCicloMobile, SemCicloSelecionado } from "../components/SeletorCicloGlobal.tsx";
import { SupervisaoChecklist } from "../components/SupervisaoChecklist.tsx";
import { RelatorioUnidadeInterno } from "../components/relatorio/RelatorioUnidadeInterno.tsx";
import { PortalUnidade } from "../portal/PortalUnidade.tsx";
import { ChecklistImpressao } from "../print/ChecklistImpressao.tsx";
import { SupervisaoImpressao } from "../print/SupervisaoImpressao.tsx";
import { Apresentacao } from "../screens/Apresentacao.tsx";
import { AuditoriaMobile } from "../screens/Auditoria.tsx";
import { Checklist } from "../screens/Checklist.tsx";
import { Ciclos } from "../screens/Ciclos.tsx";
import { Dashboard } from "../screens/Dashboard.tsx";
import { EscopoSetores } from "../screens/EscopoSetores.tsx";
import { Login } from "../screens/Login.tsx";
import { Mais } from "../screens/Mais.tsx";
import * as More from "../screens/MaisScreens.tsx";
import { Panorama } from "../screens/Panorama.tsx";
import { Placeholder } from "../screens/Placeholder.tsx";
import { PlanoAcaoInterno } from "../screens/PlanoAcao.tsx";
import { Relatorio } from "../screens/Relatorio.tsx";
import { Riscos } from "../screens/Riscos.tsx";
import { Usuarios } from "../screens/Usuarios.tsx";
import { InstallPrompt } from "./InstallPrompt.tsx";
import "../screens/screens.css";
import { exigeCicloSelecionado, podeGerirCiclo } from "../lib/ciclos.ts";
import {
  type Aba,
  type AppState,
  type QuadroPilha,
  type UsuarioAutenticado,
  useAppState,
} from "../state/useAppState.ts";

function SemCiclo({ podeAbrir, irCiclos }: { podeAbrir: boolean; irCiclos: () => void }) {
  return (
    <Fragment>
      <Header title="CAINT" sub="Aguardando abertura de ciclo" />
      <div className="m-body" style={{ display: "grid", placeItems: "center", textAlign: "center" }}>
        <div style={{ maxWidth: 300, color: "var(--text-muted)" }}>
          <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>Nenhum ciclo aberto</div>
          <p style={{ fontSize: 14, lineHeight: 1.5 }}>
            As avaliações começam quando o presidente abre um ciclo para uma unidade.
          </p>
          {podeAbrir && (
            <button type="button" className="m-btn primary" style={{ marginTop: 14 }} onClick={irCiclos}>
              Abrir ciclo
            </button>
          )}
        </div>
      </div>
    </Fragment>
  );
}

/** Sub-telas alcançadas pela pilha (hub "Mais") — todas recebem só `onBack`. */
const SUBTELAS: Record<string, (props: { onBack: () => void }) => React.ReactNode> = {
  monitoramento: More.Monitoramento,
  percepcao: More.Percepcao,
  supervisao: More.Supervisao,
  catalogo: More.Catalogo,
  matriz: More.Matriz,
  motor: More.Motor,
  escalas: More.Escalas,
  estrutura: More.Estrutura,
  versoes: More.Versoes,
};

const TABS: readonly [Aba, IconName, string][] = [
  ["inicio", "home", "Início"],
  ["checklist", "check", "Checklist"],
  ["risco", "risk", "Riscos"],
  ["relatorio", "report", "Relatório"],
  ["mais", "more", "Mais"],
];

const TITULO_ROTA: Record<string, string> = {
  unit: "Painel da Unidade",
  risco: "Riscos",
  contextuais: "Riscos Contextuais",
  plano: "Plano de Ação",
  relatorio: "Relatório Executivo",
  apresentacao: "Apresentação do Ciclo",
  panorama: "Panorama da Rede",
  monitoramento: "QACI & IMS",
  percepcao: "Percepção de Risco",
  supervisao: "Supervisão AUDIN",
  catalogo: "Catálogo",
  matriz: "Matriz de Riscos",
  motor: "Motor de Cálculo",
  escalas: "Escalas & Selos",
  estrutura: "Estrutura",
  usuarios: "Usuários & Perfis",
  auditoria: "Auditoria",
  "matriz-unidade": "Matriz da Unidade",
  versoes: "Versionamento",
};

interface TelaArgs {
  s: AppState;
  auth: UsuarioAutenticado;
  toast: (msg: string) => void;
}

function SupervisaoMobile({ s }: { s: AppState }) {
  return (
    <Fragment>
      <Header title="Supervisão AUDIN" sub="Asseguração da 3ª linha" onBack={s.back} />
      <div className="m-body">
        <SupervisaoChecklist
          dados={s.supervisao}
          podeEditar={s.podeEditarSupervisao}
          editar={s.editarSupervisao}
          enviar={s.enviarSupervisao}
          itens={s.itensEscopoSup}
          amostraAlvo={s.amostraAlvo}
          checklistCompleto={s.checklistCompleto}
          liberarAmostra={s.liberarAmostra}
          podeLiberarAmostra={s.podeLiberarAmostra}
          online={s.online}
          onImprimir={() => s.go("imprimir-sup")}
        />
      </div>
    </Fragment>
  );
}

/** Tela de escopo (Setores da Unidade) — pré-avaliação e reabertura (doc 17b §5). */
function montarEscopo(s: AppState, onPronto: () => void): React.ReactNode {
  return (
    <EscopoSetores
      escopo={s.escopo}
      items={s.items}
      alternarSetor={s.alternarSetor}
      confirmarEscopo={s.confirmarEscopo}
      editavel={s.editavel}
      onPronto={onPronto}
      unidadeId={s.avaliacaoAtual?.unidadeId}
    />
  );
}

/** Resolve a tela de uma rota empilhada (hub "Mais" e atalhos). */
function resolverPilha(
  top: QuadroPilha,
  s: AppState,
  auth: UsuarioAutenticado,
  checklist: React.ReactNode,
): React.ReactNode {
  switch (top.route) {
    case "checklist":
      return checklist;
    case "escopo":
      return montarEscopo(s, s.back);
    case "apresentacao":
      return (
        <Apresentacao
          items={s.itensEscopo}
          momento={s.momento}
          imgq={s.imgq}
          qaci={s.qaci}
          ims={s.ims}
          avaliacaoAtual={s.avaliacaoAtual}
          evolucao={s.evolucao}
          onBack={s.back}
        />
      );
    case "panorama":
      return <Panorama panorama={s.panoramaRede} onBack={s.back} />;
    case "usuarios":
      return <Usuarios papel={auth.papel} api={s.usuariosApi} onBack={s.back} euId={auth.id} />;
    case "auditoria":
      return <AuditoriaMobile token={s.token} onBack={s.back} />;
    case "relatorio":
      return (
        <Relatorio
          items={s.itensEscopo}
          momento={s.momento}
          qaci={s.qaci}
          ims={s.ims}
          avaliacaoAtual={s.avaliacaoAtual}
          token={s.token}
          evolucao={s.evolucao}
          supervisao={s.supervisao}
          carregarImgr={s.ciclosApi.resultado}
        />
      );
    case "relatorio-unidade":
      return <RelatorioUnidadeInterno avaliacaoId={s.avaliacaoAtual?.id ?? null} token={s.token} />;
    case "matriz-unidade":
      return <MatrizUpload matrizApi={s.matrizApi} onBack={s.back} />;
    case "ciclos":
      return (
        <Ciclos
          ciclosApi={s.ciclosApi}
          recarregar={s.recarregar}
          podeAbrir={podeGerirCiclo(auth.papel)}
          autor={auth.nome}
          onBack={s.back}
          avaliacaoIdAtivo={s.avaliacaoAtual?.id ?? null}
          onAtivar={(id) => {
            s.selecionar(id);
            s.back();
          }}
        />
      );
    case "imprimir":
      return <ChecklistImpressao onBack={s.back} setorInicial={top.arg} avaliador={auth.nome} />;
    case "imprimir-sup":
      return (
        <SupervisaoImpressao
          onBack={s.back}
          amostra={s.supervisao.amostra}
          unidadeId={s.avaliacaoAtual?.unidadeId}
          supervisor={auth.nome}
        />
      );
    case "supervisao":
      // Supervisão (audin) + gestão (admin/coord/presidente) veem o checklist;
      // os demais papéis veem o overview (doc 09).
      return s.podeSupervisionar ? <SupervisaoMobile s={s} /> : <More.Supervisao onBack={s.back} />;
    case "imgr":
      return (
        <Fragment>
          <Header title="IMGR" sub="Resultado do formulário público" onBack={s.back} />
          <div className="m-body">
            <ResultadosPublico ciclosApi={s.ciclosApi} />
          </div>
        </Fragment>
      );
    case "planos":
      return <PlanoAcaoInterno avaliacaoId={s.avaliacaoAtual?.id ?? null} token={s.token} onBack={s.back} />;
    default: {
      const Sub = SUBTELAS[top.route];
      return Sub ? (
        <Sub onBack={s.back} />
      ) : (
        <Placeholder title={TITULO_ROTA[top.route] ?? top.route} onBack={s.back} />
      );
    }
  }
}

/**
 * A tela pedida exige ciclo e não há nenhum selecionado? Compartilhada entre o
 * roteamento e o shell (que precisa saber para não pintar o fundo escuro da aba
 * "início" sob o gate, que é claro).
 */
function semCicloSelecionado(s: AppState): boolean {
  const alvo = s.stack[s.stack.length - 1]?.route ?? s.tab;
  return !s.avaliacaoAtual && s.avaliacoes.length > 0 && exigeCicloSelecionado(alvo);
}

/** Resolve a tela atual a partir da pilha de navegação e da aba ativa. */
function resolverTela({ s, auth, toast }: TelaArgs): React.ReactNode {
  const podeAbrir = podeGerirCiclo(auth.papel);
  // Gate (doc 17b §3.2): o checklist só abre depois do escopo confirmado.
  const checklist = s.escopo.escopoConfirmado ? (
    <Checklist
      items={s.items}
      momento={s.momento}
      setMomento={s.setMomento}
      patch={s.patch}
      toast={toast}
      evidencias={s.evidencias}
      evidenciasPendentes={s.evidenciasPendentes}
      enviarEvidencia={s.enviarEvidencia}
      excluirEvidencia={s.excluirEvidencia}
      substituirEvidencia={s.substituirEvidencia}
      removerEvidenciaPendente={s.removerEvidenciaPendente}
      editavel={s.editavel}
      avaliacaoAtual={s.avaliacaoAtual}
      setoresAtivos={s.escopo.setoresAtivos}
      onEditarEscopo={() => s.go("escopo")}
      onImprimir={(code) => s.go("imprimir", code)}
    />
  ) : (
    montarEscopo(s, () => {})
  );
  const top = s.stack[s.stack.length - 1];
  // Guardrail: nenhuma tela com escopo de ciclo abre sem ciclo selecionado — sem
  // isto ela parece editável e descarta todo o preenchimento (ver `SemCicloSelecionado`).
  const alvo = top?.route ?? s.tab;
  if (semCicloSelecionado(s)) {
    return (
      <Fragment>
        <Header
          title="Selecione um ciclo"
          sub={TITULO_ROTA[alvo] ?? "Ciclo não selecionado"}
          onBack={top ? s.back : undefined}
        />
        <div className="m-body">
          <SemCicloSelecionado
            avaliacoes={s.avaliacoes}
            avaliacaoId={null}
            onSelecionar={s.selecionar}
            onGerir={() => s.go("ciclos")}
          />
        </div>
      </Fragment>
    );
  }
  if (top) {
    return resolverPilha(top, s, auth, checklist);
  }
  if (s.tab === "mais") {
    return (
      <Mais
        auth={auth}
        online={s.online}
        pendingSync={s.pendingSync}
        syncStatus={s.syncStatus}
        sync={s.sync}
        reenviarRespostasLocais={s.reenviarRespostasLocais}
        temRespostasLocais={s.temRespostasLocais}
        trocarSenha={s.trocarSenha}
        token={s.token}
        go={s.go}
        onLogout={s.logout}
      />
    );
  }
  // Abas de campo sem nenhuma avaliação → estado vazio.
  if (s.avaliacoes.length === 0) {
    return <SemCiclo podeAbrir={podeAbrir} irCiclos={() => s.go("ciclos")} />;
  }
  switch (s.tab) {
    case "checklist":
      return checklist;
    case "risco":
      return <Riscos items={s.itensEscopo} momento={s.momento} />;
    case "relatorio":
      return (
        <Relatorio
          items={s.itensEscopo}
          momento={s.momento}
          qaci={s.qaci}
          ims={s.ims}
          avaliacaoAtual={s.avaliacaoAtual}
          token={s.token}
          evolucao={s.evolucao}
          supervisao={s.supervisao}
          carregarImgr={s.ciclosApi.resultado}
        />
      );
    default:
      return (
        <Dashboard
          auth={auth}
          items={s.itensEscopo}
          qaci={s.qaci}
          ims={s.ims}
          go={s.go}
          onLogout={s.logout}
          avaliacaoAtual={s.avaliacaoAtual}
        />
      );
  }
}

export function AppMobile() {
  const s = useAppState();
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2400);
  }, []);

  if (s.unidadeSession) {
    // Conta de unidade: login único → só a view do Plano de Ação.
    return (
      <PortalUnidade
        token={s.unidadeSession.token}
        nome={s.unidadeSession.nome}
        cargo={s.unidadeSession.cargo}
        onSair={s.logout}
      />
    );
  }

  if (!s.auth) {
    return (
      <MobileShell dark>
        <Login
          onSignIn={s.signIn}
          aviso={s.sessaoExpirada ? "Sua sessão expirou. Entre novamente para continuar." : undefined}
        />
        <InstallPrompt />
      </MobileShell>
    );
  }

  const top = s.stack[s.stack.length - 1];
  // O gate de ciclo é uma tela clara — não pode herdar o fundo escuro do início.
  const isDark = !top && s.tab === "inicio" && !semCicloSelecionado(s);
  const screen = resolverTela({ s, auth: s.auth, toast });

  return (
    <MobileShell dark={isDark}>
      <div className="m-app">
        <BarraCicloMobile
          avaliacoes={s.avaliacoes}
          avaliacaoId={s.avaliacaoAtual?.id ?? null}
          onSelecionar={s.selecionar}
          onGerir={() => s.go("ciclos")}
        />
        {screen}
        {toastMsg && <div className="m-toast">{toastMsg}</div>}
        <InstallPrompt />
        <div className="m-tabbar">
          {TABS.map(([k, ic, lbl]) => {
            const ativa = s.tab === k && !top;
            return (
              <button
                key={k}
                type="button"
                className={`m-tab${ativa ? " on" : ""}`}
                onClick={() => s.switchTab(k)}
              >
                <Icon name={ic} size={24} stroke={ativa ? 2.2 : 1.8} />
                <span>{lbl}</span>
              </button>
            );
          })}
        </div>
      </div>
    </MobileShell>
  );
}
